import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";

const URL_SELF = `${SITE.url}/api/v1`;

export const Route = createFileRoute("/api/v1")({
  head: () => ({
    meta: [
      { title: `Map Observer Public API — Local search data, free with attribution` },
      { name: "description", content: "Free public JSON API: Google algorithm updates, Map Pack volatility, and Google Business Profile categories. CC-BY-style attribution required." },
      { property: "og:title", content: "Map Observer Public API" },
      { property: "og:description", content: "Free JSON endpoints for algorithm updates, Map Pack volatility, and GBP categories." },
      { property: "og:url", content: URL_SELF },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
  }),
  component: Page,
});

function Endpoint({ method, path, desc, example }: { method: string; path: string; desc: string; example: string }) {
  return (
    <div className="border border-border p-5 mt-4">
      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-wider px-2 py-0.5 bg-paper-soft border border-border">{method}</span>
        <code className="text-sm">{path}</code>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
      <pre className="mt-3 text-xs bg-paper-soft border border-border p-3 overflow-x-auto"><code>{example}</code></pre>
    </div>
  );
}

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
        <p className="eyebrow">Developer reference</p>
        <h1 className="headline-display text-4xl mt-3">Map Observer Public API</h1>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
          Free JSON endpoints for the data behind Map Observer&rsquo;s trackers: confirmed and
          suspected algorithm updates, Map Pack volatility scores, and the Google Business
          Profile category list. No key, no signup, no rate-limit headers — just a link back
          to <code>mapobserver.com</code> wherever you use the data.
        </p>

        <section className="mt-10">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">Terms (the short version)</h2>
          <ul className="mt-4 list-disc pl-5 text-sm space-y-1">
            <li>Free for any use — dashboards, articles, internal tools, side projects.</li>
            <li>Include a visible link to the relevant Map Observer page (we&rsquo;ll list it in <code>attribution.url</code> on every response).</li>
            <li>Don&rsquo;t hammer it — endpoints are cached for 10&ndash;30 minutes; if you need more, email us.</li>
            <li>We may rate-limit abusive clients at the edge.</li>
          </ul>
        </section>

        <section className="mt-10">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">Endpoints</h2>

          <Endpoint
            method="GET"
            path="/api/public/v1/updates?limit=50"
            desc="Confirmed and suspected Google local-search algorithm updates, newest first."
            example={`curl https://mapobserver.com/api/public/v1/updates?limit=10`}
          />

          <Endpoint
            method="GET"
            path="/api/public/v1/volatility?limit=52"
            desc="Weekly Map Pack volatility scores (0–10) with level classification (calm → turbulent)."
            example={`curl https://mapobserver.com/api/public/v1/volatility?limit=12`}
          />

          <Endpoint
            method="GET"
            path="/api/public/v1/categories?q=plumber&limit=25"
            desc="Search the Google Business Profile category list. Omit q to list all categories."
            example={`curl "https://mapobserver.com/api/public/v1/categories?q=dentist&limit=10"`}
          />
        </section>

        <section className="mt-10">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">Also available</h2>
          <ul className="mt-4 list-disc pl-5 text-sm space-y-1">
            <li><a href="/rss.xml" className="text-accent underline">RSS feed</a> — published articles</li>
            <li><a href="/news-sitemap.xml" className="text-accent underline">Google News sitemap</a></li>
            <li><a href="/llms-full.txt" className="text-accent underline">llms-full.txt</a> — full-text feed for AI crawlers</li>
            <li><a href="/embeds" className="text-accent underline">Embeddable widgets</a> — drop-in iframes with do-follow attribution</li>
          </ul>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
